import { useTranslation } from 'next-i18next'

import { useRouter } from 'next/router'

import ResponsiveImage from '~/components/Image/ResponsiveImage'

import CurriculumOnboardingWrapper from '~/curriculum/components/CurriculumOnboardingWrapper'
import { MediaQueryType } from '~/components/Image/types'
import Button from '~/components/Buttons/Button'
import MqOnboardingHeader from '~/onboarding/components/mappingQuiz/MqOnboardingHeader'
import MindButton from '~/onboarding/components/buttons/MindButton'
import { useAppSelector } from '~/redux/hooks'
import { selectCurriculumSubjectContent } from '~/curriculum/slice/curriculum.slice'
import { selectUserId } from '~/auth/slices/auth.slice'
import { EventDetectionMethod, EventType } from '~/behavior-tracking/types'
import { logUserActivity } from '~/behavior-tracking/utility/view-event-logs.utility'
import { extractCountryCodeAndGradeIdFromRouterPath } from '~/curriculum/utils/extractQueryParams'
import ROUTES from '~/shared/constants/routes.const'

export default function MappingQuizQuestionCompletionPage({
  onMappingQuizCompletion,
}: {
  onMappingQuizCompletion?: () => void
}): React.ReactElement {
  const router = useRouter()
  const mediaQuery: MediaQueryType[] = [
    { screen: '(max-width: 767px)', width: '100vw' },
    { screen: '(min-width: 768px)', width: '415px' },
  ]
  const { t } = useTranslation(['dashboard', 'curriculum'])
  const subjectContent = useAppSelector(selectCurriculumSubjectContent)
  const subjectCurriculumPageUrl = subjectContent.subjectUrl || ROUTES.DASHBOARD
  const userId = useAppSelector(selectUserId)
  const { countryCode, gradeLevel } = extractCountryCodeAndGradeIdFromRouterPath(router)

  const logFinishMappingQuizEvent = async (): Promise<void> => {
    if (userId) {
      const event = {
        eventName: EventType.CURRICULUM_EVENT,
        viewKey: router.asPath,
        extra: {
          detectBy: EventDetectionMethod.ON_MAPPING_QUIZ_FINISH,
          grade: gradeLevel,
          topic: subjectContent.subjectLabel,
          countryCode,
          subjectOrder: subjectContent.subjectOrder,
        },
      }
      await logUserActivity(userId, event)
    }
  }

  const handleGetStartedClick = (): void => {
    logFinishMappingQuizEvent()
    if (onMappingQuizCompletion) {
      onMappingQuizCompletion()
    }
  }

  return (
    <CurriculumOnboardingWrapper bgGradient="bg-gradient-purple-dark">
      <section
        className={`flex flex-col justify-center items-center w-full rounded md:space-y-12 mt-5 max-h-screen-9/10 h-screen
        `}
      >
        <MindButton
          className="absolute top-4 right-2 w-full md:hidden"
          imageClassName="w-20"
          tosterMessage={t('curriculum:mindGotUpdated')}
        />

        <div className="w-full mt-auto">
          <div className="relative flex-1 flex flex-col items-center ">
            <ResponsiveImage
              alt="knowledge-base-icon"
              className="w-83"
              fit="contain"
              height={550}
              imageClassName="w-full -rotate-30"
              imageWidths="480"
              mediaQuery={mediaQuery}
              srcKey="landing/mind-ai-v2.png"
              width={864}
            />
            <div className="flex flex-col mx-6 pt-4">
              <MqOnboardingHeader
                className="text-7.5 font-bold leading-9 text-center"
                translationKey="curriculum:mappingQuiz.generatedRecipe"
              />

              <MqOnboardingHeader
                className="text-7.5 font-bold leading-9 text-center pt-6 md:pt-2"
                translationKey="curriculum:mappingQuiz.personalTrack"
                transValues={{ subject: subjectContent.subjectLabel }}
              />
            </div>
          </div>
        </div>

        <Button
          className="flex mt-auto hover:scale-110 items-center bg-orange w-64 h-12 rounded text-gray-footer text-center !font-bold border border-white shadow-lg tracking-px leading-8"
          href={subjectCurriculumPageUrl}
          onClick={handleGetStartedClick}
        >
          {t('curriculum:mappingQuiz.getStarted')}
        </Button>
      </section>
    </CurriculumOnboardingWrapper>
  )
}
